import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import './RoleSelector.css';

const RoleSelector = ({ selectedRoles, onRoleSelect, agents, openSignal, validationMessage, onDialogClose, accentColor = '#4D8BFF' }) => {
  const { t, i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const accent = accentColor || '#4D8BFF';
  const lastOpenSignalRef = useRef(openSignal);

  // 仅在外部主动递增 openSignal 时弹出；综合训练页切走再回来会整棵卸载重挂，不能把旧 signal 当成新一次打开。
  useEffect(() => {
    if (openSignal && openSignal !== lastOpenSignalRef.current) {
      setIsDropdownOpen(true);
    }
    lastOpenSignalRef.current = openSignal;
  }, [openSignal]);

  const closeDialog = () => {
    setIsDropdownOpen(false);
    onDialogClose?.();
  };

  // 获取显示名称的函数（根据当前语言选择中文名或英文名）
  const getDisplayName = (agent) => {
    const isZhCN = !i18n.language || i18n.language === 'zh-CN';
    // 非中文环境下优先使用英文名
    if (!isZhCN && agent.name_en) return agent.name_en;
    // 中文环境或无英文名时，使用中文名
    if (agent.display_name) return agent.display_name;
    if (agent.chn_name) return agent.chn_name;
    return agent.name;
  };

  // 检查角色是否被选中
  const isRoleSelected = (agent) => {
    return selectedRoles.includes(agent.type);
  };

  // 角色选择处理
  const handleSelect = (agent) => {
    onRoleSelect(agent.type);
  };

  return (
    <div className="role-selector">
      <span className="role-label">{t('team_label')}</span>
      <div className="custom-dropdown">
        <div 
          className={`dropdown-trigger ${isDropdownOpen ? 'open' : ''}`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          {selectedRoles.length > 0 ? (
            <span className="selected-roles">
              {t('team_selected_count', { count: selectedRoles.length })}
            </span>
          ) : (
            <span className="placeholder">{t('team_min_roles')}</span>
          )}
          <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
        </div>
        
        <Dialog
          open={isDropdownOpen}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              closeDialog();
            }
          }}
          maxWidth={false} 
          className="workbench-dialog team-member-dialog"
          PaperProps={{
            className: 'workbench-dialog-paper',
            style: { '--workbench-accent': accent },
            sx: {
              width: 'calc(100vw - 80px)',
              maxWidth: '1100px',
              height: 'calc(100vh - 120px)',
              maxHeight: '700px',
              margin: '32px auto',
              bgcolor: '#1e1e1e',
              color: '#e7e7e7',
              borderRadius: '10px',
              border: '1px solid #3e3e42',
              boxShadow: '0 18px 48px rgba(0, 0, 0, 0.38)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: '#202124',
            color: '#e7e7e7',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderTop: `2px solid ${accent}`,
            borderBottom: '1px solid #3e3e42'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <GroupAddIcon sx={{ color: accent, fontSize: 22 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#e7e7e7', lineHeight: 1.2 }}>
                  {t('team_dialog_title')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#a6afb8', letterSpacing: '0.2px' }}>
                  {t('team_selected_members', { selected: selectedRoles.length, total: agents.length })}
                </Typography>
              </Box>
            </Box>
            
            <IconButton
              onClick={closeDialog}
              sx={{
                color: '#a6afb8',
                '&:hover': { color: '#fff', bgcolor: '#303238' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ 
            bgcolor: '#1e1e1e',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            flex: 1
          }}>
            <Box sx={{ 
              padding: '16px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              flex: 1
            }}>
              {/* 校验失败提示 - 由外部输入框校验触发，选够角色或关闭对话框后自动消失 */}
              {validationMessage && (
                <Box sx={{
                  p: 1.5,
                  bgcolor: 'rgba(245, 158, 11, 0.12)',
                  borderRadius: '8px',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}>
                  <InfoOutlinedIcon sx={{ color: '#f59e0b', fontSize: 20, mt: 0.3 }} />
                  <Typography variant="body2" sx={{ color: '#fde68a', lineHeight: 1.5, fontWeight: 500 }}>
                    {validationMessage}
                  </Typography>
                </Box>
              )}

              {/* 提示信息 - 优化背景色和图标对齐 */}
              <Box sx={{
                p: 1.5,
                bgcolor: '#292a2d',
                borderRadius: '6px',
                border: `1px solid ${accent}66`,
                display: 'flex',
                alignItems: 'flex-start', // 图标与第一行文字对齐
                gap: 1.5
              }}>
                <InfoOutlinedIcon sx={{ color: accent, fontSize: 20, mt: 0.3 }} />
                <Typography variant="body2" sx={{ color: '#c3c9d0', lineHeight: 1.5 }}>
                  {t('team_hint')}
                </Typography>
              </Box>

              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'flex-start',
                py: 2
              }}>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '1008px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    columnGap: '16px',
                    rowGap: '16px',
                    justifyContent: 'center',
                    px: 2
                  }}
                >
                  {agents.map((agent) => {
                    const selected = isRoleSelected(agent);
                    return (
                      <Paper
                        key={agent.name || agent.type}
                        onClick={() => handleSelect(agent)}
                        elevation={0}
                        sx={{
                          bgcolor: selected ? `${accent}1f` : '#292a2d',
                          border: selected ? `2px solid ${accent}` : '1px solid #3e3e42',
                          borderRadius: '8px',
                          p: 2,
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'border-color 0.16s ease, background-color 0.16s ease',
                          minHeight: '160px',
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          opacity: 1,
                          '&:hover': {
                            borderColor: selected ? accent : '#5b6068',
                            bgcolor: selected ? `${accent}2a` : '#303238'
                          }
                        }}
                      >
                        <Box sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          opacity: selected ? 1 : 0,
                          transition: 'opacity 0.16s ease',
                        }}>
                          <CheckCircleIcon sx={{ color: accent, fontSize: 22 }} />
                        </Box>

                        <Typography variant="subtitle1" sx={{ 
                          fontWeight: 700, 
                          color: selected ? accent : '#e7e7e7',
                          mb: 2, // 增加间距
                          pr: 3, 
                          height: '28px', 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {getDisplayName(agent)}
                        </Typography>

                        <Typography variant="body2" sx={{ 
                          color: '#b8c0c8',
                          fontSize: '0.875rem',
                          lineHeight: 1.6, // 增加行高
                          mb: 1.5,
                          flex: 1,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2, 
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {((!i18n.language || i18n.language === 'zh-CN') ? agent.description : (agent.description_en || agent.description)) || t('team_no_desc')}
                        </Typography>

                        <Box sx={{ mt: 'auto', overflow: 'hidden' }}>
                          <Chip 
                            label={agent.type} 
                            size="small" 
                            sx={{ 
                              bgcolor: '#202124',
                              color: selected ? accent : '#a6afb8',
                              fontSize: '0.7rem',
                              height: 22,
                              borderRadius: '6px',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              border: '1px solid',
                              borderColor: selected ? `${accent}66` : '#3e3e42',
                              '& .MuiChip-label': { 
                                px: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }
                            }} 
                          />
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default RoleSelector;
